<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
const props = defineProps({
  members: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
  canManage: { type: Boolean, default: true },
  performAction: { type: Function, required: true },
});
const { t } = useI18n();
const selectedAgentId = ref('');
const memberIds = computed(
  () => new Set(props.members.users.map(member => member.user.id))
);
const availableAgents = computed(() =>
  props.agents.filter(agent => !memberIds.value.has(agent.id))
);
const addMember = async () => {
  if (!selectedAgentId.value) return;
  if (
    await props.performAction('addMember', {
      user_id: Number(selectedAgentId.value),
    })
  )
    selectedAgentId.value = '';
};
const removeMember = member =>
  props.performAction('removeMember', {
    membershipId: member.id,
    type: 'user',
  });
</script>

<template>
  <section class="border-t border-n-weak pt-4">
    <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
      {{ t('INTERNAL_CHAT.MEMBERS') }}
    </h3>
    <div v-if="canManage" class="mb-3 flex items-center gap-2">
      <label class="min-w-0 flex-1">
        <span class="sr-only">{{ t('INTERNAL_CHAT.ADD_MEMBER') }}</span>
        <Select
          v-model="selectedAgentId"
          class="!w-full [&_select]:w-full"
          :options="
            availableAgents.map(agent => ({
              value: agent.id,
              label: agent.name || agent.email,
            }))
          "
          :placeholder="t('INTERNAL_CHAT.SELECT_AGENT')"
        />
      </label>
      <Button
        type="button"
        size="sm"
        variant="faded"
        color="slate"
        :label="t('INTERNAL_CHAT.ADD')"
        :disabled="!selectedAgentId"
        @click="addMember"
      />
    </div>
    <ul class="m-0 grid gap-1 p-0">
      <li
        v-for="member in members.users"
        :key="member.id"
        class="flex items-center justify-between gap-3 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm"
      >
        <span class="min-w-0 truncate text-n-slate-12">
          {{
            member.user.display_name || member.user.name || member.user.email
          }}
          <span class="ms-1 text-xs text-n-slate-10">
            {{ t('INTERNAL_CHAT.MEMBER_ROLE', { role: member.role }) }}
          </span>
        </span>
        <Button
          v-if="canManage"
          type="button"
          variant="ghost"
          color="ruby"
          size="xs"
          class="shrink-0"
          :label="t('INTERNAL_CHAT.REMOVE')"
          :aria-label="t('INTERNAL_CHAT.REMOVE_MEMBER')"
          @click="removeMember(member)"
        />
      </li>
      <li
        v-for="member in members.teams"
        :key="`team-${member.id}`"
        class="rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
      >
        {{ member.team.name }}
      </li>
    </ul>
  </section>
</template>
